import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useField } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useCollectionOptionsQuery } from "@/partner/entities/contents";
import { ContentTypeType } from "@/types/graphql/partner.generated";

interface CollectionsFieldProps {
  /** Имя поля формы (react-final-form), по умолчанию collectionIds */
  name?: string;
  /** Исключить коллекцию из опций (сама коллекция в своей форме) */
  excludeId?: string | null;
}

// Порт референсного CollectionsField: мультиселект «в каких коллекциях
// лежит контент» (загружает до 1000 коллекций, поиск клиентский — MUI).
export const CollectionsField: FC<CollectionsFieldProps> = ({
  name = "collectionIds",
  excludeId = null,
}) => {
  const { t } = useTranslation();
  const { input } = useField<string[]>(name);
  const value: string[] = input.value || [];

  const { data, loading } = useCollectionOptionsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      page: 0,
      rowsPerPage: 1000,
      filter: { type: ContentTypeType.CollectionType },
    },
  });
  const options = (data?.contents?.nodes ?? []).filter(
    (option) => option.id !== excludeId
  );

  return (
    <Autocomplete
      multiple
      openOnFocus
      fullWidth
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      autoComplete
      autoHighlight
      disableCloseOnSelect
      filterSelectedOptions
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={options.filter((option) => value.includes(option.id))}
      onChange={(_event, newValue) => {
        input.onChange(newValue.map((option) => option.id));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={t("collections.field.label")}
        />
      )}
    />
  );
};
